import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  ArrowLeft,
  Clock,
  User,
  AlertCircle,
  Mail,
  Phone,
  Calendar,
  FileText,
  Send,
  Paperclip,
  MoreVertical,
  Edit,
  Trash2,
  Link as LinkIcon,
  Loader2,
  StickyNote,
  Video,
  Upload,
  ExternalLink,
  CheckCircle,
  XCircle,
  Plus,
  DollarSign,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400 border-blue-500/50' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
  waiting: { label: 'Waiting', color: 'bg-purple-500/20 text-purple-400 border-purple-500/50' },
  closed: { label: 'Closed', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-zinc-500/20 text-zinc-400' },
  medium: { label: 'Medium', color: 'bg-blue-500/20 text-blue-400' },
  high: { label: 'High', color: 'bg-orange-500/20 text-orange-400' },
  urgent: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' }
};

const ACTIVITY_ICONS = {
  email_sent: Mail,
  email_received: Mail,
  note: StickyNote,
  phone_call: Phone,
  meeting: Video,
  file_uploaded: Paperclip,
  status_change: AlertCircle,
  assignment_change: User,
  record_linked: LinkIcon,
  ticket_created: MessageSquare
};

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [linkedRecords, setLinkedRecords] = useState({ estimates: [], invoices: [], orders: [] });
  
  // Dialog states
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showCallDialog, setShowCallDialog] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  
  // Form states
  const [noteContent, setNoteContent] = useState('');
  const [callForm, setCallForm] = useState({ summary: '', duration_minutes: 15, direction: 'inbound', contact_name: '' });
  const [meetingForm, setMeetingForm] = useState({ summary: '', meeting_date: '', duration_minutes: 60, attendees: '', location: '' });
  const [emailForm, setEmailForm] = useState({ to_email: '', subject: '', body: '', attach_estimates: [], attach_invoices: [] });
  const [linkForm, setLinkForm] = useState({ record_type: 'estimate', record_id: '' });
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkableRecords, setLinkableRecords] = useState([]);
  const [loadingLinkRecords, setLoadingLinkRecords] = useState(false);
  
  // Estimate form state
  const [estimateItems, setEstimateItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [estimateNotes, setEstimateNotes] = useState('');
  const [products, setProducts] = useState([]);
  
  // Available estimates/invoices for attachment
  const [availableEstimates, setAvailableEstimates] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  
  const [submitting, setSubmitting] = useState(false);
  
  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchTicket();
    fetchActivities();
    fetchStaff();
    fetchProducts();
  }, [ticketId]);

  // Fetch available documents AFTER ticket is loaded (filtered by customer)
  useEffect(() => {
    if (ticket?.customer_id) {
      fetchAvailableDocuments(ticket.customer_id);
    }
  }, [ticket?.customer_id]);

  // Fetch linkable records when dialog opens or type/search changes
  useEffect(() => {
    if (showLinkDialog) {
      fetchLinkableRecords();
    }
  }, [showLinkDialog, linkForm.record_type, linkSearchQuery]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products?limit=100`, authHeaders);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Failed to load products');
    }
  };

  const fetchLinkableRecords = async () => {
    setLoadingLinkRecords(true);
    try {
      const res = await axios.get(`${API}/admin/tickets/search-records`, {
        ...authHeaders,
        params: {
          record_type: linkForm.record_type,
          search: linkSearchQuery
        }
      });
      setLinkableRecords(res.data.records || []);
    } catch (err) {
      console.error('Failed to load linkable records:', err);
      setLinkableRecords([]);
    } finally {
      setLoadingLinkRecords(false);
    }
  };

  const fetchTicket = async () => {
    try {
      const res = await axios.get(`${API}/admin/tickets/${ticketId}`, authHeaders);
      setTicket(res.data);
      
      // Pre-fill email form with customer email
      if (res.data.customer_email) {
        setEmailForm(prev => ({ ...prev, to_email: res.data.customer_email }));
      }
      
      // Fetch linked records details
      fetchLinkedRecords(res.data.linked_records);
    } catch (err) {
      toast.error('Failed to load ticket');
      navigate('/admin/tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const res = await axios.get(`${API}/admin/tickets/${ticketId}/activities`, authHeaders);
      setActivities(res.data.activities || []);
    } catch (err) {
      console.error('Failed to load activities');
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/admin/staff`, authHeaders);
      setStaff(res.data.staff || []);
    } catch (err) {
      console.error('Failed to load staff');
    }
  };

  const fetchAvailableDocuments = async (customerId) => {
    if (!customerId) return;
    
    try {
      // Fetch estimates for this customer only
      const estRes = await axios.get(`${API}/admin/estimates?customer_id=${customerId}`, authHeaders);
      setAvailableEstimates(estRes.data.estimates || estRes.data || []);
    } catch (err) {
      console.error('Failed to load estimates');
    }
    
    try {
      // Fetch invoices for this customer only
      const invRes = await axios.get(`${API}/admin/invoices?customer_id=${customerId}`, authHeaders);
      setAvailableInvoices(invRes.data.invoices || invRes.data || []);
    } catch (err) {
      console.error('Failed to load invoices');
    }
  };

  const fetchLinkedRecords = async (links) => {
    const records = { estimates: [], invoices: [], orders: [] };
    
    // Fetch linked estimates
    for (const id of (links?.estimates || [])) {
      try {
        const res = await axios.get(`${API}/admin/estimates/${id}`, authHeaders);
        if (res.data) records.estimates.push(res.data);
      } catch (err) {}
    }
    
    // Fetch linked invoices
    for (const id of (links?.invoices || [])) {
      try {
        const res = await axios.get(`${API}/admin/invoices/${id}`, authHeaders);
        if (res.data) records.invoices.push(res.data);
      } catch (err) {}
    }
    
    // Fetch linked orders
    for (const id of (links?.orders || [])) {
      try {
        const res = await axios.get(`${API}/admin/orders/${id}`, authHeaders);
        if (res.data) records.orders.push(res.data);
      } catch (err) {}
    }
    
    setLinkedRecords(records);
  };

  const updateTicket = async (updates) => {
    try {
      const res = await axios.put(`${API}/admin/tickets/${ticketId}`, updates, authHeaders);
      setTicket(res.data);
      toast.success('Ticket updated');
      fetchActivities();
    } catch (err) {
      toast.error('Failed to update ticket');
    }
  };

  const addNote = async () => {
    if (!noteContent.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/tickets/${ticketId}/notes`, { content: noteContent }, authHeaders);
      toast.success('Note added');
      setNoteContent('');
      setShowNoteDialog(false);
      fetchActivities();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const logCall = async () => {
    if (!callForm.summary.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/tickets/${ticketId}/calls`, callForm, authHeaders);
      toast.success('Call logged');
      setCallForm({ summary: '', duration_minutes: 15, direction: 'inbound', contact_name: '' });
      setShowCallDialog(false);
      fetchActivities();
    } catch (err) {
      toast.error('Failed to log call');
    } finally {
      setSubmitting(false);
    }
  };

  const logMeeting = async () => {
    if (!meetingForm.summary.trim() || !meetingForm.meeting_date) return;
    setSubmitting(true);
    try {
      const payload = {
        ...meetingForm,
        attendees: meetingForm.attendees.split(',').map(a => a.trim()).filter(Boolean)
      };
      await axios.post(`${API}/admin/tickets/${ticketId}/meetings`, payload, authHeaders);
      toast.success('Meeting logged');
      setMeetingForm({ summary: '', meeting_date: '', duration_minutes: 60, attendees: '', location: '' });
      setShowMeetingDialog(false);
      fetchActivities();
    } catch (err) {
      toast.error('Failed to log meeting');
    } finally {
      setSubmitting(false);
    }
  };

  const sendEmail = async () => {
    if (!emailForm.to_email || !emailForm.subject || !emailForm.body) {
      toast.error('Please fill all email fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        to_email: emailForm.to_email,
        subject: emailForm.subject,
        body: emailForm.body,
        attach_estimates: emailForm.attach_estimates.length > 0 ? emailForm.attach_estimates : null,
        attach_invoices: emailForm.attach_invoices.length > 0 ? emailForm.attach_invoices : null
      };
      const res = await axios.post(`${API}/admin/tickets/${ticketId}/email`, payload, authHeaders);
      toast.success(res.data.message || 'Email sent');
      setEmailForm(prev => ({ ...prev, subject: '', body: '', attach_estimates: [], attach_invoices: [] }));
      setShowEmailDialog(false);
      fetchActivities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEstimateAttachment = (estimateId) => {
    setEmailForm(prev => ({
      ...prev,
      attach_estimates: prev.attach_estimates.includes(estimateId)
        ? prev.attach_estimates.filter(id => id !== estimateId)
        : [...prev.attach_estimates, estimateId]
    }));
  };

  const toggleInvoiceAttachment = (invoiceId) => {
    setEmailForm(prev => ({
      ...prev,
      attach_invoices: prev.attach_invoices.includes(invoiceId)
        ? prev.attach_invoices.filter(id => id !== invoiceId)
        : [...prev.attach_invoices, invoiceId]
    }));
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/admin/tickets/${ticketId}/files`, formData, {
        ...authHeaders,
        headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded');
      fetchActivities();
    } catch (err) {
      toast.error('Failed to upload file');
    }
    
    e.target.value = '';
  };

  const linkRecord = async () => {
    if (!linkForm.record_id.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/tickets/${ticketId}/link`, linkForm, authHeaders);
      toast.success(`${linkForm.record_type} linked`);
      setLinkForm({ record_type: 'estimate', record_id: '' });
      setShowLinkDialog(false);
      fetchTicket();
      fetchActivities();
    } catch (err) {
      toast.error('Failed to link record');
    } finally {
      setSubmitting(false);
    }
  };

  const unlinkRecord = async (type, id) => {
    try {
      await axios.delete(`${API}/admin/tickets/${ticketId}/link/${type}/${id}`, authHeaders);
      toast.success('Record unlinked');
      fetchTicket();
    } catch (err) {
      toast.error('Failed to unlink');
    }
  };

  const deleteTicket = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/admin/tickets/${ticketId}`, authHeaders);
      toast.success('Ticket deleted');
      navigate('/admin/tickets');
    } catch (err) {
      toast.error('Failed to delete ticket');
    } finally {
      setSubmitting(false);
      setShowDeleteDialog(false);
    }
  };

  const addEstimateItem = () => {
    setEstimateItems([...estimateItems, { description: '', quantity: 1, unit_price: 0 }]);
  };

  const removeEstimateItem = (index) => {
    if (estimateItems.length > 1) {
      setEstimateItems(estimateItems.filter((_, i) => i !== index));
    }
  };

  const updateEstimateItem = (index, field, value) => {
    const updated = [...estimateItems];
    updated[index][field] = value;
    // Auto-calculate total
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].total = (updated[index].quantity || 0) * (updated[index].unit_price || 0);
    }
    setEstimateItems(updated);
  };

  const selectProductForItem = (index, productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const updated = [...estimateItems];
      updated[index] = {
        product_id: product.id,
        description: product.name,
        sku: product.sku,
        quantity: 1,
        unit_price: product.pricing?.retail || 0,
        total: product.pricing?.retail || 0
      };
      setEstimateItems(updated);
    }
  };

  const createEstimate = async () => {
    // Validate items
    const validItems = estimateItems.filter(item => item.description && item.unit_price > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with a price');
      return;
    }

    // Need a customer - check if ticket has customer_id
    if (!ticket.customer_id) {
      toast.error('This ticket has no linked customer. Please link a customer first.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customer_id: ticket.customer_id,
        items: validItems.map(item => ({
          product_id: item.product_id || null,
          description: item.description,
          sku: item.sku || '',
          quantity: item.quantity || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          total: (item.quantity || 1) * (parseFloat(item.unit_price) || 0)
        })),
        notes: estimateNotes || `Created from ticket ${ticket.ticket_number}`,
        source_ticket_id: ticket.id  // Link to this ticket
      };

      const res = await axios.post(`${API}/admin/estimates`, payload, authHeaders);
      toast.success(`Estimate ${res.data.estimate?.estimate_number} created and linked!`);
      
      // Reset form
      setEstimateItems([{ description: '', quantity: 1, unit_price: 0 }]);
      setEstimateNotes('');
      setShowEstimateDialog(false);
      
      // Refresh ticket to show linked estimate
      fetchTicket();
      fetchActivities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getActivityIcon = (type) => {
    const IconComponent = ACTIVITY_ICONS[type] || MessageSquare;
    return IconComponent;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-[#78BE20]" />
        </div>
      </AdminLayout>
    );
  }

  if (!ticket) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Ticket not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="ticket-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin/tickets')}
              className="mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Tickets
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{ticket.subject}</h1>
              <Badge variant="outline" className={STATUS_CONFIG[ticket.status]?.color}>
                {STATUS_CONFIG[ticket.status]?.label}
              </Badge>
              <Badge variant="outline" className={PRIORITY_CONFIG[ticket.priority]?.color}>
                {PRIORITY_CONFIG[ticket.priority]?.label}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              {ticket.ticket_number} • Created {formatDate(ticket.created_at)}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={ticket.status} onValueChange={(v) => updateTicket({ status: v })}>
              <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="waiting">Waiting</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={ticket.priority} onValueChange={(v) => updateTicket({ priority: v })}>
              <SelectTrigger className="w-[120px] bg-zinc-800 border-zinc-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>

            <Select value={ticket.visibility || 'public'} onValueChange={(v) => updateTicket({ visibility: v })}>
              <SelectTrigger className="w-[120px] bg-zinc-800 border-zinc-700" data-testid="ticket-visibility-toggle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
              data-testid="delete-ticket-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Timeline */}
          <div className="lg:col-span-2 space-y-4">
            {/* Quick Actions */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowNoteDialog(true)}>
                    <StickyNote className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowCallDialog(true)}>
                    <Phone className="w-4 h-4 mr-2" />
                    Log Call
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowMeetingDialog(true)}>
                    <Video className="w-4 h-4 mr-2" />
                    Log Meeting
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowEmailDialog(true)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload File
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={uploadFile}
                  />
                  <Button size="sm" variant="outline" onClick={() => setShowLinkDialog(true)}>
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Link Record
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setShowEstimateDialog(true)}
                    className="border-[#78BE20]/50 text-[#78BE20] hover:bg-[#78BE20]/10"
                    data-testid="create-estimate-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Create Estimate
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Activity Timeline */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">Activity Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {activities.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No activities yet</p>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity) => {
                      const IconComponent = getActivityIcon(activity.type);
                      const isEmail = activity.type.includes('email');
                      const isIncoming = activity.type === 'email_received';
                      
                      return (
                        <div 
                          key={activity.id} 
                          className={`flex gap-4 p-4 rounded-lg ${
                            isIncoming ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-zinc-800/50'
                          }`}
                        >
                          <div className={`p-2 rounded-full h-fit ${
                            isIncoming ? 'bg-blue-500/20' : 'bg-zinc-700'
                          }`}>
                            <IconComponent className={`w-4 h-4 ${
                              isIncoming ? 'text-blue-400' : 'text-zinc-400'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">
                                {activity.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(activity.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {activity.content}
                            </p>
                            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                              <div className="mt-2 text-xs text-muted-foreground">
                                {activity.metadata.from && <span>From: {activity.metadata.from}</span>}
                                {activity.metadata.to && <span>To: {activity.metadata.to}</span>}
                                {activity.metadata.duration_minutes && (
                                  <span>Duration: {activity.metadata.duration_minutes} min</span>
                                )}
                              </div>
                            )}
                            {activity.attachments?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {activity.attachments.map((att, i) => (
                                  <a
                                    key={i}
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs text-[#78BE20] hover:underline"
                                  >
                                    <Paperclip className="w-3 h-3" />
                                    {att.filename}
                                  </a>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              by {activity.created_by || 'System'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Customer Info */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Customer</span>
                  {ticket.customer_id && (
                    <Badge variant="outline" className="border-green-500/50 text-green-400 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Linked
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>{ticket.customer_name || 'Unknown'}</span>
                </div>
                {ticket.customer_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${ticket.customer_email}`} className="text-[#78BE20] hover:underline">
                      {ticket.customer_email}
                    </a>
                  </div>
                )}
                <div className="text-sm text-muted-foreground">
                  Source: {ticket.source?.replace(/_/g, ' ')}
                </div>
                {ticket.customer_id && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2"
                    onClick={() => navigate(`/admin/customers/${ticket.customer_id}`)}
                  >
                    <ExternalLink className="w-3 h-3 mr-2" />
                    View Customer Profile
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Assignment */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">Assignment</CardTitle>
              </CardHeader>
              <CardContent>
                <Select 
                  value={ticket.assigned_to || 'unassigned'} 
                  onValueChange={(v) => updateTicket({ assigned_to: v === 'unassigned' ? null : v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.email}>
                        {s.first_name} {s.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Linked Records */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">Linked Records</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Estimates */}
                {linkedRecords.estimates.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-400" />
                      Estimates
                    </h4>
                    {linkedRecords.estimates.map((est) => (
                      <div key={est.id} className="bg-zinc-800 rounded-lg mb-2 overflow-hidden">
                        <div className="flex items-center justify-between p-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{est.estimate_number}</span>
                            <Badge variant="outline" className="text-xs">{est.status}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-[#78BE20]">${est.total?.toFixed(2)}</span>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6"
                              onClick={() => navigate(`/admin/estimates/${est.id}`)}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6 text-red-400"
                              onClick={() => unlinkRecord('estimate', est.id)}
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {/* Show if converted to invoice */}
                        {est.converted_to_invoice_id && (
                          <div className="bg-zinc-900/50 px-3 py-1 flex items-center gap-2 text-xs text-muted-foreground">
                            <ArrowRight className="w-3 h-3" />
                            Converted to Invoice
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Invoices */}
                {linkedRecords.invoices.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      Invoices
                    </h4>
                    {linkedRecords.invoices.map((inv) => (
                      <div key={inv.id} className="bg-zinc-800 rounded-lg mb-2 overflow-hidden">
                        <div className="flex items-center justify-between p-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{inv.invoice_number}</span>
                            <Badge variant="outline" className={`text-xs ${
                              inv.payment_status === 'paid' ? 'border-green-500 text-green-400' :
                              inv.payment_status === 'partial' ? 'border-yellow-500 text-yellow-400' :
                              'border-zinc-500'
                            }`}>{inv.payment_status}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-[#78BE20]">${inv.total?.toFixed(2)}</span>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6"
                              onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6 text-red-400"
                              onClick={() => unlinkRecord('invoice', inv.id)}
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {/* Show source if from estimate */}
                        {inv.source_estimate_id && (
                          <div className="bg-zinc-900/50 px-3 py-1 flex items-center gap-2 text-xs text-muted-foreground">
                            <ArrowRight className="w-3 h-3 rotate-180" />
                            From Estimate
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Orders */}
                {linkedRecords.orders.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-orange-400" />
                      Orders
                    </h4>
                    {linkedRecords.orders.map((ord) => (
                      <div key={ord.id || ord.order_number} className="flex items-center justify-between p-2 bg-zinc-800 rounded-lg mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{ord.order_number}</span>
                          <Badge variant="outline" className="text-xs">{ord.status}</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-6 w-6"
                            onClick={() => navigate(`/admin/orders/${ord.id || ord.order_number}`)}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-6 w-6 text-red-400"
                            onClick={() => unlinkRecord('order', ord.id || ord.order_number)}
                          >
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {linkedRecords.estimates.length === 0 && 
                 linkedRecords.invoices.length === 0 && 
                 linkedRecords.orders.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No linked records
                  </p>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => setShowLinkDialog(true)}
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Link Record
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Add Note Dialog */}
        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Add Note</DialogTitle>
            </DialogHeader>
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Enter your note..."
              className="bg-zinc-800 border-zinc-700 min-h-[150px]"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
              <Button onClick={addNote} disabled={submitting} className="bg-[#78BE20]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Note'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Log Call Dialog */}
        <Dialog open={showCallDialog} onOpenChange={setShowCallDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Log Phone Call</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Direction</Label>
                  <Select value={callForm.direction} onValueChange={(v) => setCallForm({ ...callForm, direction: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inbound">Inbound</SelectItem>
                      <SelectItem value="outbound">Outbound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={callForm.duration_minutes}
                    onChange={(e) => setCallForm({ ...callForm, duration_minutes: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={callForm.contact_name}
                  onChange={(e) => setCallForm({ ...callForm, contact_name: e.target.value })}
                  placeholder="Who did you speak with?"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Summary</Label>
                <Textarea
                  value={callForm.summary}
                  onChange={(e) => setCallForm({ ...callForm, summary: e.target.value })}
                  placeholder="Call summary..."
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCallDialog(false)}>Cancel</Button>
              <Button onClick={logCall} disabled={submitting} className="bg-[#78BE20]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Call'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Log Meeting Dialog */}
        <Dialog open={showMeetingDialog} onOpenChange={setShowMeetingDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Log Meeting</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={meetingForm.meeting_date}
                    onChange={(e) => setMeetingForm({ ...meetingForm, meeting_date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={meetingForm.duration_minutes}
                    onChange={(e) => setMeetingForm({ ...meetingForm, duration_minutes: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={meetingForm.location}
                  onChange={(e) => setMeetingForm({ ...meetingForm, location: e.target.value })}
                  placeholder="Office, Zoom, etc."
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Attendees (comma separated)</Label>
                <Input
                  value={meetingForm.attendees}
                  onChange={(e) => setMeetingForm({ ...meetingForm, attendees: e.target.value })}
                  placeholder="John, Jane, ..."
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Summary</Label>
                <Textarea
                  value={meetingForm.summary}
                  onChange={(e) => setMeetingForm({ ...meetingForm, summary: e.target.value })}
                  placeholder="Meeting notes..."
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMeetingDialog(false)}>Cancel</Button>
              <Button onClick={logMeeting} disabled={submitting} className="bg-[#78BE20]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Meeting'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Send Email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>To</Label>
                <Input
                  type="email"
                  value={emailForm.to_email}
                  onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Re: ..."
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  placeholder="Your message..."
                  className="bg-zinc-800 border-zinc-700 min-h-[150px]"
                />
              </div>
              
              {/* Attachments Section */}
              <div className="space-y-3 border-t border-zinc-800 pt-4">
                <Label className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attach Documents
                </Label>
                
                {/* Estimates */}
                {availableEstimates.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Estimates:</p>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                      {availableEstimates.slice(0, 10).map((est) => (
                        <label
                          key={est.id || est.estimate_number}
                          className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            emailForm.attach_estimates.includes(est.id || est.estimate_number)
                              ? 'bg-[#78BE20]/20 border-[#78BE20]'
                              : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={emailForm.attach_estimates.includes(est.id || est.estimate_number)}
                            onChange={() => toggleEstimateAttachment(est.id || est.estimate_number)}
                            className="sr-only"
                          />
                          <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{est.estimate_number}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {est.customer_name} - ${est.total?.toFixed(2)}
                            </div>
                          </div>
                          {emailForm.attach_estimates.includes(est.id || est.estimate_number) && (
                            <CheckCircle className="w-4 h-4 text-[#78BE20] flex-shrink-0" />
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Invoices */}
                {availableInvoices.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Invoices:</p>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                      {availableInvoices.slice(0, 10).map((inv) => (
                        <label
                          key={inv.id || inv.invoice_number}
                          className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                            emailForm.attach_invoices.includes(inv.id || inv.invoice_number)
                              ? 'bg-[#78BE20]/20 border-[#78BE20]'
                              : 'bg-zinc-800 border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={emailForm.attach_invoices.includes(inv.id || inv.invoice_number)}
                            onChange={() => toggleInvoiceAttachment(inv.id || inv.invoice_number)}
                            className="sr-only"
                          />
                          <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{inv.invoice_number}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {inv.customer_name} - ${inv.total?.toFixed(2)}
                            </div>
                          </div>
                          {emailForm.attach_invoices.includes(inv.id || inv.invoice_number) && (
                            <CheckCircle className="w-4 h-4 text-[#78BE20] flex-shrink-0" />
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {availableEstimates.length === 0 && availableInvoices.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">No documents available to attach</p>
                )}
                
                {/* Selected attachments summary */}
                {(emailForm.attach_estimates.length > 0 || emailForm.attach_invoices.length > 0) && (
                  <div className="bg-[#78BE20]/10 border border-[#78BE20]/20 rounded p-2">
                    <p className="text-sm text-[#78BE20]">
                      <Paperclip className="w-3 h-3 inline mr-1" />
                      {emailForm.attach_estimates.length + emailForm.attach_invoices.length} document(s) will be attached as PDF
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
              <Button onClick={sendEmail} disabled={submitting} className="bg-[#78BE20]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Record Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={(open) => {
          setShowLinkDialog(open);
          if (!open) {
            setLinkSearchQuery('');
            setLinkableRecords([]);
            setLinkForm({ record_type: 'estimate', record_id: '' });
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl">
            <DialogHeader>
              <DialogTitle>Link Record</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Record Type</Label>
                <Select 
                  value={linkForm.record_type} 
                  onValueChange={(v) => {
                    setLinkForm({ record_type: v, record_id: '' });
                    setLinkSearchQuery('');
                  }}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estimate">Estimate</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="order">Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Search {linkForm.record_type === 'order' ? 'Orders' : `${linkForm.record_type.charAt(0).toUpperCase() + linkForm.record_type.slice(1)}s`}</Label>
                <Input
                  value={linkSearchQuery}
                  onChange={(e) => setLinkSearchQuery(e.target.value)}
                  placeholder={`Search by number, customer name...`}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Select Record</Label>
                <div className="max-h-64 overflow-y-auto space-y-2 border border-zinc-800 rounded-lg p-2 bg-zinc-950/50">
                  {loadingLinkRecords ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-[#78BE20]" />
                    </div>
                  ) : linkableRecords.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No {linkForm.record_type}s found{linkSearchQuery ? ` matching "${linkSearchQuery}"` : ''}
                    </p>
                  ) : (
                    linkableRecords.map((record) => (
                      <div
                        key={record.id}
                        onClick={() => setLinkForm({ ...linkForm, record_id: record.id })}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          linkForm.record_id === record.id
                            ? 'bg-[#78BE20]/20 border border-[#78BE20]'
                            : 'bg-zinc-800 border border-transparent hover:border-zinc-700'
                        }`}
                        data-testid={`link-record-option-${record.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-sm">{record.number}</div>
                            <div className="text-xs text-muted-foreground">{record.customer_name}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-[#78BE20]">${record.total?.toFixed(2) || '0.00'}</div>
                            <Badge variant="outline" className="text-xs mt-1">{record.status}</Badge>
                          </div>
                        </div>
                        {linkForm.record_id === record.id && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-[#78BE20]">
                            <CheckCircle className="w-3 h-3" />
                            Selected
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {linkForm.record_id && (
                <div className="bg-[#78BE20]/10 border border-[#78BE20]/20 rounded-lg p-3">
                  <p className="text-sm text-[#78BE20] flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Ready to link selected {linkForm.record_type}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
              <Button 
                onClick={linkRecord} 
                disabled={submitting || !linkForm.record_id} 
                className="bg-[#78BE20]"
                data-testid="link-record-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Link Record'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Ticket Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-red-400">Delete Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Are you sure you want to delete ticket <span className="font-semibold text-white">{ticket?.ticket_number}</span>?
              </p>
              <p className="text-sm text-muted-foreground">
                This will permanently delete the ticket and all its activities. This action cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button 
                onClick={deleteTicket} 
                disabled={submitting}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-ticket"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" /> Delete Ticket</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Estimate Dialog */}
        <Dialog open={showEstimateDialog} onOpenChange={(open) => {
          setShowEstimateDialog(open);
          if (!open) {
            setEstimateItems([{ description: '', quantity: 1, unit_price: 0 }]);
            setEstimateNotes('');
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#78BE20]" />
                Create Estimate from Ticket
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Customer</p>
                <p className="font-medium">{ticket?.customer_name || 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">{ticket?.customer_email}</p>
                {!ticket?.customer_id && (
                  <p className="text-sm text-orange-400 mt-2">
                    ⚠️ No customer linked to this ticket. Please link a customer first.
                  </p>
                )}
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button size="sm" variant="outline" onClick={addEstimateItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {estimateItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end bg-zinc-800/30 p-3 rounded-lg">
                      <div className="col-span-5">
                        <Label className="text-xs">Description</Label>
                        <div className="space-y-1">
                          <Select onValueChange={(v) => selectProductForItem(index, v)}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-xs h-8">
                              <SelectValue placeholder="Select product..." />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} - ${p.pricing?.retail?.toFixed(2)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={item.description}
                            onChange={(e) => updateEstimateItem(index, 'description', e.target.value)}
                            placeholder="Or type custom description"
                            className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateEstimateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Unit Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updateEstimateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Total</Label>
                        <div className="h-8 flex items-center text-sm font-medium text-[#78BE20]">
                          ${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}
                        </div>
                      </div>
                      <div className="col-span-1">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 text-red-400 hover:text-red-300"
                          onClick={() => removeEstimateItem(index)}
                          disabled={estimateItems.length === 1}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t border-zinc-700 pt-3 space-y-1 text-right">
                  <div className="text-sm text-muted-foreground">
                    Subtotal: ${estimateItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || 0)), 0).toFixed(2)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    GST (10%): ${(estimateItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || 0)), 0) * 0.1).toFixed(2)}
                  </div>
                  <div className="text-lg font-bold text-[#78BE20]">
                    Total: ${(estimateItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || 0)), 0) * 1.1).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={estimateNotes}
                  onChange={(e) => setEstimateNotes(e.target.value)}
                  placeholder="Additional notes for the estimate..."
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>

              {/* Info Box */}
              <div className="bg-[#78BE20]/10 border border-[#78BE20]/20 rounded-lg p-3">
                <p className="text-sm text-[#78BE20] flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  This estimate will be automatically linked to ticket {ticket?.ticket_number}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  When converted to invoice, the invoice will also be linked to this ticket.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEstimateDialog(false)}>Cancel</Button>
              <Button 
                onClick={createEstimate} 
                disabled={submitting || !ticket?.customer_id}
                className="bg-[#78BE20] hover:bg-[#6aa819]"
                data-testid="create-estimate-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-2" /> Create Estimate</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
